/**
 * ForgotPasswordForm (Iteration 12 — N9)
 * 
 * Email input, submit, security-hardened confirmation.
 * LAT-24: identical message for known/unknown emails.
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export default function ForgotPasswordForm({ onBack }) {
  const theme = useTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    try {
      // In production, this would call Cognito forgotPassword
      // For now, simulate the call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 2 }}>
          {/* LAT-24: identical for known/unknown */}
          If an account exists, a reset link has been sent.
        </Alert>
        <Button onClick={onBack} sx={{ textTransform: 'none' }}>
          Back to login
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Enter your email address and we'll send you a reset link.
      </Typography>

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        fullWidth
        variant="outlined"
        sx={{
          '& .MuiInputBase-root': {
            minHeight: theme.spacing(14), // 56px — exceeds 44px WCAG floor
          },
        }}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading}
        sx={{
          minHeight: theme.spacing(14), // 56px
          textTransform: 'none',
          fontWeight: 600,
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
      </Button>

      <Button onClick={onBack} sx={{ textTransform: 'none' }}>
        Back to login
      </Button>
    </Box>
  );
}
